import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Send, Phone, Mail } from "lucide-react";

export const ContactForm = () => {
  const { toast } = useToast();
  const [eventType, setEventType] = useState("firmowe");
  const [budget, setBudget] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    try {
      await axios({
        method: 'POST',
        url: 'https://form.aigency.pl/api/form',
        data: {
          website: 'WystepFirmowy.pl',
          email: formData.get('email'),
          destinationEmail: 'kontakt@wystepfirmowy.pl',
          fields: {
            "Imię i nazwisko": formData.get('name'),
            "Numer telefonu": formData.get('phone'),
            "Nazwa firmy": formData.get('company'),
            "Rodzaj wydarzenia": eventType,
            "Budżet orientacyjny": budget,
            "Wiadomość": formData.get('message'),
          }
        }
      });

      toast({
        title: "Formularz wysłany!",
        description: "Skontaktujemy się z Tobą najszybciej jak to możliwe.",
      });
      form.reset();
    } catch (error: any) {
      if (error.response?.status === 422) {
        toast({
          title: "Błąd walidacji",
          description: error.response.data.message || "Sprawdź poprawność danych",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Wystąpił błąd",
          description: "Przepraszamy, spróbuj ponownie później",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-primary relative overflow-hidden" id="kontakt">
      <div className="absolute inset-0">
        <div className="absolute w-[500px] h-[500px] bg-accent/5 rounded-full -bottom-48 -left-48 blur-3xl" />
      </div>

      <div className="container max-w-5xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-accent font-semibold text-sm tracking-widest uppercase">Kontakt</span>
          <h2 className="text-4xl md:text-5xl font-montserrat font-bold mt-3 text-white">
            Zamów występ
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-5 gap-12">
          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="md:col-span-2 space-y-8"
          >
            <div>
              <h3 className="text-white font-montserrat font-bold text-xl mb-4">Porozmawiajmy</h3>
              <p className="text-white/50 leading-relaxed">
                Opisz swoje wydarzenie, a my przygotujemy idealny występ dopasowany do Twoich potrzeb.
              </p>
            </div>
            <div className="space-y-4">
              <a href="tel:+48533333993" className="flex items-center gap-3 text-white/70 hover:text-accent transition-colors group">
                <div className="w-10 h-10 rounded-xl bg-white/5 group-hover:bg-accent/10 flex items-center justify-center transition-colors">
                  <Phone className="w-5 h-5" />
                </div>
                <span>+48 533 333 993</span>
              </a>
              <a href="mailto:kontakt@wystepfirmowy.pl" className="flex items-center gap-3 text-white/70 hover:text-accent transition-colors group">
                <div className="w-10 h-10 rounded-xl bg-white/5 group-hover:bg-accent/10 flex items-center justify-center transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <span>kontakt@wystepfirmowy.pl</span>
              </a>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="md:col-span-3"
          >
            <form onSubmit={handleSubmit} className="space-y-5 bg-white/5 border border-white/10 p-8 rounded-2xl backdrop-blur-sm">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white/70 text-sm mb-2 block">Imię i nazwisko</label>
                  <Input name="name" required className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-accent rounded-xl" />
                </div>
                <div>
                  <label className="text-white/70 text-sm mb-2 block">Email</label>
                  <Input name="email" type="email" required className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-accent rounded-xl" />
                </div>
              </div>

              <div>
                <label className="text-white/70 text-sm mb-2 block">Numer telefonu</label>
                <Input name="phone" type="tel" required className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-accent rounded-xl" />
              </div>
              <div>
                <label className="text-white/70 text-sm mb-2 block">Nazwa firmy (opcjonalne)</label>
                <Input name="company" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-accent rounded-xl" />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white/70 text-sm mb-3 block">Rodzaj wydarzenia</label>
                  <RadioGroup defaultValue="firmowe" onValueChange={setEventType} className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="firmowe" id="firmowe" className="border-white/20 text-accent" />
                      <Label htmlFor="firmowe" className="text-white/70 cursor-pointer">Firmowe</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="prywatne" id="prywatne" className="border-white/20 text-accent" />
                      <Label htmlFor="prywatne" className="text-white/70 cursor-pointer">Prywatne</Label>
                    </div>
                  </RadioGroup>
                </div>
                <div>
                  <label className="text-white/70 text-sm mb-2 block">Budżet orientacyjny</label>
                  <Select onValueChange={setBudget}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-xl">
                      <SelectValue placeholder="Wybierz budżet" />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(240,33%,14%)] border-white/10 text-white">
                      <SelectItem value="do 5 000 PLN">do 5 000 PLN</SelectItem>
                      <SelectItem value="od 5 000 do 10 000 PLN">od 5 000 do 10 000 PLN</SelectItem>
                      <SelectItem value="od 10 000 do 20 000 PLN">od 10 000 do 20 000 PLN</SelectItem>
                      <SelectItem value="od 20 000 do 50 000 PLN">od 20 000 do 50 000 PLN</SelectItem>
                      <SelectItem value="od 50 000 do 100 000 PLN">od 50 000 do 100 000 PLN</SelectItem>
                      <SelectItem value="powyżej 100 000 PLN">powyżej 100 000 PLN</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-white/70 text-sm mb-2 block">Wiadomość</label>
                <Textarea name="message" required className="min-h-[120px] bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-accent rounded-xl" />
              </div>

              <Button
                type="submit"
                className="w-full bg-accent hover:bg-accent-light text-white font-semibold py-6 rounded-xl text-base transition-all hover:scale-[1.02]"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Wysyłanie..." : (
                  <span className="flex items-center gap-2">
                    Wyślij zapytanie
                    <Send className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>
          </motion.div>
        </div>

        {/* Company info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-20 pt-10 border-t border-white/10 text-center"
        >
          <p className="text-white/30 text-xs tracking-widest uppercase mb-3">Właścicielem serwisu jest</p>
          <p className="text-white/60 font-montserrat font-semibold text-sm">Wirtualny Biznes</p>
          <p className="text-white/40 text-sm mt-1">ul. Księcia Witolda 49/15, 50-202 Wrocław</p>
          <p className="text-white/40 text-sm mt-1">NIP: 8961619199</p>
        </motion.div>
      </div>
    </section>
  );
};
